import { useState, useEffect, useRef, useCallback } from "react";
import { Archive, Upload, FileText, Trash2, Loader2, Sparkles, ExternalLink, Eye } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import InfoHint from "./InfoHint";

/**
 * KI-Dokumentenarchiv (Feb-2026, User-Wunsch):
 *  - Bis zu 6 Dateien gleichzeitig (PDF/Bild, je max. 10 MB) hochladen.
 *  - Jede Datei wird per KI (Gemini) klassifiziert (COC / ZB1 / ZB2 / Kaufvertrag /
 *    Rechnung / Übergabeprotokoll / Versicherung / Sonstiges) und im Inserat-Archiv
 *    abgelegt. Nutzt den bestehenden Endpoint /dispo/upload-document (kind="other").
 *  - Auto-Verknüpfung: erkanntes COC/ZB1/ZB2 setzt automatisch den passenden
 *    „erhalten"-Haken im Formular (via update-Prop).
 */
const KIND_LABELS = {
    zb1: { label: "ZB1 (Fahrzeugschein)", color: "bg-blue-100 text-blue-800" },
    zb2: { label: "ZB2 (Fahrzeugbrief)", color: "bg-emerald-100 text-emerald-800" },
    coc: { label: "COC", color: "bg-violet-100 text-violet-800" },
    sale_contract: { label: "Kaufvertrag", color: "bg-amber-100 text-amber-800" },
    invoice: { label: "Rechnung", color: "bg-cyan-100 text-cyan-800" },
    handover_protocol: { label: "Übergabeprotokoll", color: "bg-indigo-100 text-indigo-800" },
    insurance: { label: "Versicherung", color: "bg-rose-100 text-rose-800" },
    other: { label: "Sonstiges", color: "bg-gray-100 text-gray-700" },
};

const MAX_FILES = 6;
const MAX_SIZE = 10 * 1024 * 1024;
const PUBLIC_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export default function DocumentArchiveSection({ listingId, form, update }) {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState([]); // [{name, status, kind, confidence}]
    const [preview, setPreview] = useState(null); // {url, name, kind} — Inline-Vorschau
    const inputRef = useRef(null);

    const load = useCallback(async () => {
        if (!listingId) { setLoading(false); return; }
        setLoading(true);
        try {
            const { data } = await api.get(`/listings/${listingId}/dispo/documents`);
            setDocs(Array.isArray(data?.documents) ? data.documents : []);
        } catch { setDocs([]); }
        finally { setLoading(false); }
    }, [listingId]);

    useEffect(() => { load(); }, [load]);

    const autoLink = (kind) => {
        // Auto-Verknüpfung: erkanntes COC/ZB1/ZB2 markiert den passenden Haken.
        if (typeof update !== "function" || !["coc", "zb1", "zb2"].includes(kind)) return;
        update(`has_${kind}`, true);
        if (!form || !form[`has_${kind}_date`]) update(`has_${kind}_date`, new Date().toISOString().slice(0, 10));
        toast.success(`${kind.toUpperCase()} automatisch als „erhalten" markiert`);
    };

    const handleFiles = async (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        if (files.length > MAX_FILES) {
            toast.error(`Maximal ${MAX_FILES} Dateien gleichzeitig.`);
            return;
        }
        const valid = [];
        for (const f of files) {
            if (f.size > MAX_SIZE) { toast.error(`„${f.name}" ist größer als 10 MB.`); continue; }
            valid.push(f);
        }
        if (!valid.length) return;
        setBusy(true);
        setProgress(valid.map((f) => ({ name: f.name, status: "pending", kind: null, confidence: null })));
        for (let i = 0; i < valid.length; i++) {
            const f = valid[i];
            setProgress((p) => p.map((x, idx) => (idx === i ? { ...x, status: "uploading" } : x)));
            try {
                const fd = new FormData();
                fd.append("file", f);
                fd.append("kind", "other"); // KI klassifiziert
                const { data } = await api.post(`/listings/${listingId}/dispo/upload-document`, fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                const cls = data?.classification || {};
                const kind = data?.document?.kind || cls.kind || "other";
                setProgress((p) => p.map((x, idx) => (idx === i ? { ...x, status: "done", kind, confidence: cls.confidence ?? null } : x)));
                autoLink(kind);
            } catch {
                setProgress((p) => p.map((x, idx) => (idx === i ? { ...x, status: "error" } : x)));
            }
        }
        setBusy(false);
        await load();
        if (inputRef.current) inputRef.current.value = "";
        setTimeout(() => setProgress([]), 4000);
    };

    const removeDoc = async (url) => {
        if (!window.confirm("Dokument aus dem Archiv entfernen?")) return;
        try {
            await api.delete(`/listings/${listingId}/dispo/documents`, { data: { url } });
            await load();
            toast.success("Dokument entfernt");
        } catch { toast.error("Entfernen fehlgeschlagen"); }
    };

    const kindBadge = (kind) => KIND_LABELS[kind] || KIND_LABELS.other;

    // Feb-2026 (User-Wunsch) — Schnell-Aktion: erkannte Rechnung/Kaufvertrag
    // direkt im passenden Disposition-Schritt öffnen (die Buttons leben in
    // DispositionActions, das in derselben Sektion gerendert ist).
    const openDispoStep = (testId) => {
        const btn = document.querySelector(`[data-testid="${testId}"]`);
        if (btn) {
            try { btn.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* noop */ }
            btn.click();
        } else {
            toast.error("Schritt nicht gefunden — bitte den Bereich Disposition öffnen.");
        }
    };
    const dispoStepFor = (kind) => (
        kind === "invoice" ? { testId: "dispo-btn-invoice", label: "Im Rechnungs-Schritt öffnen" }
            : kind === "sale_contract" ? { testId: "dispo-btn-contract", label: "Im Kaufvertrag öffnen" }
                : kind === "handover_protocol" ? { testId: "dispo-btn-handover", label: "Im Schritt Übergabe & TÜV öffnen" }
                    : null
    );

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-3" data-testid="document-archive-section">
            <div className="flex items-center gap-2 mb-1">
                <Archive className="w-4 h-4 text-[#111827]" />
                <span className="font-bold text-[13px] uppercase tracking-wider text-[#111827]">KI-Dokumentenarchiv</span>
                <InfoHint id="dispo.doc_archive" side="bottom" defaultText={`Belege hochladen (COC, ZB1, ZB2 …) — die KI erkennt automatisch den Dokumenttyp. Bis zu ${MAX_FILES} Dateien, je max. 10 MB.`} />
            </div>

            <label
                className={`flex flex-col items-center justify-center gap-1 border-2 border-dashed rounded-sm py-4 px-3 cursor-pointer transition-colors ${busy ? "border-gray-200 bg-gray-50 opacity-70 pointer-events-none" : "border-emerald-300 bg-emerald-50/50 hover:bg-emerald-50"}`}
                data-testid="document-archive-dropzone"
            >
                <input
                    ref={inputRef}
                    type="file"
                    multiple
                    accept=".pdf,image/png,image/jpeg,image/webp"
                    className="hidden"
                    disabled={busy}
                    onChange={(e) => handleFiles(e.target.files)}
                    data-testid="document-archive-input"
                />
                {busy ? <Loader2 className="w-5 h-5 text-emerald-600 animate-spin" /> : <Upload className="w-5 h-5 text-emerald-600" />}
                <span className="text-[13px] font-bold text-gray-700">{busy ? "Verarbeite Dokumente…" : "Dateien auswählen oder hierher ziehen"}</span>
                <span className="text-[10px] text-gray-500">PDF, JPG, PNG, WEBP</span>
            </label>

            {progress.length > 0 && (
                <ul className="mt-3 space-y-1.5" data-testid="document-archive-progress">
                    {progress.map((p, i) => (
                        <li key={i} className="flex items-center gap-2 text-[12px] bg-white border border-gray-200 rounded-sm px-2.5 py-1.5" data-testid={`document-progress-${i}`}>
                            {p.status === "done" ? <Sparkles className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                : p.status === "error" ? <span className="text-red-600 shrink-0">✕</span>
                                    : <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin shrink-0" />}
                            <span className="truncate flex-1">{p.name}</span>
                            {p.status === "done" && (
                                <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${kindBadge(p.kind).color}`}>
                                    {kindBadge(p.kind).label}
                                    {p.confidence != null && p.kind !== "other" && <span className="font-normal opacity-70">{Math.round(p.confidence * 100)}%</span>}
                                </span>
                            )}
                            {p.status === "done" && dispoStepFor(p.kind) && (
                                <button type="button" onClick={() => openDispoStep(dispoStepFor(p.kind).testId)} className="text-[10px] font-bold text-[#111827] underline hover:text-black shrink-0" data-testid={`document-progress-step-${i}`}>
                                    {dispoStepFor(p.kind).label}
                                </button>
                            )}
                            {p.status === "error" && <span className="text-[10px] font-bold text-red-600">Fehler</span>}
                        </li>
                    ))}
                </ul>
            )}

            <div className="mt-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">
                    Archivierte Dokumente {loading && docs.length === 0 ? "" : `(${docs.length})`}
                </div>
                {loading && docs.length === 0 ? (
                    <div className="flex items-center gap-2 text-[11px] text-gray-500 py-1" data-testid="document-archive-loading">
                        <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />
                        Dokumente werden geladen…
                    </div>
                ) : docs.length === 0 ? (
                    <p className="text-[11px] text-gray-400" data-testid="document-archive-empty">Noch keine Dokumente archiviert.</p>
                ) : (
                    <ul className="space-y-1.5" data-testid="document-archive-list">
                        {docs.map((d, i) => (
                            <li key={d.url || i} className="flex items-center gap-2 bg-white border border-gray-200 rounded-sm px-2.5 py-2 text-[12px]" data-testid={`document-archive-item-${i}`}>
                                <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                                <span className="truncate flex-1 font-medium">{d.name || "Dokument"}</span>
                                <span className={`inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded-sm shrink-0 ${kindBadge(d.kind).color}`} data-testid={`document-archive-kind-${i}`}>
                                    {kindBadge(d.kind).label}
                                </span>
                                {dispoStepFor(d.kind) && (
                                    <button
                                        type="button"
                                        onClick={() => openDispoStep(dispoStepFor(d.kind).testId)}
                                        className="text-[10px] font-bold text-[#111827] underline hover:text-black shrink-0 hidden sm:inline"
                                        title={dispoStepFor(d.kind).label}
                                        data-testid={`document-archive-step-${i}`}
                                    >
                                        {d.kind === "invoice" ? "→ Rechnung" : d.kind === "sale_contract" ? "→ Vertrag" : "→ Übergabe"}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => setPreview({ url: `${PUBLIC_BASE}${d.url}`, name: d.name || "Dokument", kind: d.kind })}
                                    className="text-[#111827] hover:text-black shrink-0"
                                    title="Vorschau anzeigen"
                                    data-testid={`document-archive-preview-${i}`}
                                >
                                    <Eye className="w-4 h-4" />
                                </button>
                                <a
                                    href={`${PUBLIC_BASE}${d.url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#111827] hover:text-black shrink-0"
                                    title="Öffnen"
                                    data-testid={`document-archive-open-${i}`}
                                >
                                    <ExternalLink className="w-4 h-4" />
                                </a>
                                <button
                                    type="button"
                                    onClick={() => removeDoc(d.url)}
                                    className="text-gray-400 hover:text-red-600 shrink-0"
                                    title="Entfernen"
                                    data-testid={`document-archive-remove-${i}`}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {preview && (
                <div
                    className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4"
                    onClick={() => setPreview(null)}
                    data-testid="document-preview-overlay"
                >
                    <div className="bg-white rounded-sm w-full max-w-4xl h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="document-preview-modal">
                        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-200">
                            <span className={`inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${kindBadge(preview.kind).color}`}>{kindBadge(preview.kind).label}</span>
                            <span className="font-bold text-sm truncate flex-1">{preview.name}</span>
                            <a href={preview.url} target="_blank" rel="noopener noreferrer" className="text-[#111827] hover:text-black" title="In neuem Tab öffnen" data-testid="document-preview-open"><ExternalLink className="w-4 h-4" /></a>
                            <button type="button" onClick={() => setPreview(null)} className="text-gray-500 hover:text-black text-xl leading-none px-1" title="Schließen" data-testid="document-preview-close">×</button>
                        </div>
                        <div className="flex-1 bg-gray-100 overflow-hidden">
                            {/\.(png|jpe?g|webp|gif)$/i.test(preview.url) ? (
                                <img src={preview.url} alt={preview.name} className="w-full h-full object-contain" />
                            ) : (
                                <iframe src={preview.url} title={preview.name} className="w-full h-full border-0" />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
